import { keyed } from "lit/directives/keyed.js";
import "../components/cpl-code-editor";
import "../components/cpl-markdown-editor";
import { TableColumns } from "../components/table-columns";
import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  api,
  type SkillDetail,
  type SkillFileSummary,
  type SkillRecord,
  type SkillTextFile,
} from "../api/client";
import { i18n } from "../i18n";
import { dateTime } from "../view-model";
import "../components/app-dialog";
import "../components/a2k-file-tree";
import { clampPage, pageCount, pageItems } from "../components/app-pagination";

@customElement("skills-view")
export class SkillsView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private skills: SkillRecord[] = [];
  @state() private page = 1;
  @state() private loading = true;
  @state() private busy = "";
  @state() private error = "";
  @state() private success = "";
  @state() private deleteTarget: SkillRecord | null = null;
  @state() private previewTarget: SkillRecord | null = null;
  @state() private preview: SkillDetail | null = null;
  @state() private previewLoading = false;
  @state() private previewError = "";
  @state() private selectedFile: SkillTextFile | null = null;
  @state() private editorContent = "";
  @state() private fileLoading = false;
  @state() private fileSaving = false;
  @state() private fileError = "";
  private previewRequest = 0;
  private fileRequest = 0;
  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    window.addEventListener("keydown", this.handleEscape);
    void this.load();
  }
  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    window.removeEventListener("keydown", this.handleEscape);
    super.disconnectedCallback();
  }
  private languageChanged = () => this.requestUpdate();
  private handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.previewTarget) this.closePreview();
  };
  private async load() {
    this.loading = true;
    this.error = "";
    try {
      this.skills = (await api.skills()).items;
      this.page = clampPage(this.page, this.skills.length);
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.loading = false;
    }
  }
  private async toggle(skill: SkillRecord) {
    this.busy = skill.name;
    try {
      await api.updateSkill(skill.name, !skill.enabled);
      this.success = i18n.t(skill.enabled ? "skillDisabled" : "skillEnabled");
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.busy = "";
    }
  }
  private async removeSkill(skill: SkillRecord) {
    this.deleteTarget = skill;
  }
  private async openPreview(skill: SkillRecord) {
    const request = ++this.previewRequest;
    this.previewTarget = skill;
    this.preview = null;
    this.previewError = "";
    this.previewLoading = true;
    try {
      const preview = (await api.skill(skill.name)).skill;
      if (request === this.previewRequest) {
        this.preview = preview;
        this.selectedFile = preview.preview;
        this.editorContent = preview.preview.content;
      }
    } catch (error) {
      if (request === this.previewRequest) {
        this.previewError = (error as Error).message;
      }
    } finally {
      if (request === this.previewRequest) this.previewLoading = false;
    }
  }
  private closePreview() {
    this.previewRequest += 1;
    this.previewTarget = null;
    this.preview = null;
    this.previewError = "";
    this.previewLoading = false;
    this.selectedFile = null;
    this.editorContent = "";
    this.fileError = "";
    this.fileLoading = false;
    this.fileSaving = false;
    this.fileRequest += 1;
  }
  private safeFiles() {
    return (this.preview?.files || []).filter(
      (file) =>
        !file.path.startsWith("/") &&
        !/^[a-z]:[\\/]/i.test(file.path) &&
        !file.path.split("/").some((part) => part === ".."),
    );
  }
  private async selectFile(file: SkillFileSummary) {
    if (!this.previewTarget || !file.editable || this.fileSaving) return;
    const request = ++this.fileRequest;
    this.fileLoading = true;
    this.fileError = "";
    try {
      const selected = (await api.skillFile(this.previewTarget.name, file.path))
        .file;
      if (request === this.fileRequest) {
        this.selectedFile = selected;
        this.editorContent = selected.content;
      }
    } catch (error) {
      if (request === this.fileRequest) {
        this.fileError = (error as Error).message;
      }
    } finally {
      if (request === this.fileRequest) this.fileLoading = false;
    }
  }
  private fileEditor() {
    if (!this.selectedFile) return null;
    const path = this.selectedFile.path.toLowerCase();
    const label = i18n.t("skillFileContent", { path: this.selectedFile.path });
    const changed = (event: CustomEvent<{ value: string }>) => {
      this.editorContent = event.detail.value;
    };
    if (/\.(md|markdown|mdx)$/.test(path))
      return html` <cpl-markdown-editor
        .value=${this.editorContent}
        .label=${label}
        .disabled=${this.fileSaving}
        .minHeight=${"22rem"}
        @value-change=${changed}
      ></cpl-markdown-editor>`;
    const language = /\.py$/.test(path)
      ? "python"
      : /\.(js|mjs|cjs|ts|tsx|jsx)$/.test(path)
        ? "javascript"
        : /\.json$/.test(path)
          ? "json"
          : /\.(sh|bash)$/.test(path)
            ? "bash"
            : "text";
    return html`<cpl-code-editor
      .value=${this.editorContent}
      .language=${language}
      .label=${label}
      .disabled=${this.fileSaving}
      .minHeight=${"22rem"}
      @value-change=${changed}
    ></cpl-code-editor>`;
  }

  private cancelEdit() {
    if (this.selectedFile) this.editorContent = this.selectedFile.content;
    this.fileError = "";
  }
  private async saveFile() {
    if (!this.previewTarget || !this.selectedFile || !this.isDirty) return;
    this.fileSaving = true;
    this.fileError = "";
    try {
      const saved = (
        await api.updateSkillFile(
          this.previewTarget.name,
          this.selectedFile.path,
          this.editorContent,
          this.selectedFile.sha256,
        )
      ).file;
      this.selectedFile = saved;
      this.editorContent = saved.content;
      this.success = i18n.t("skillFileSaved", { path: saved.path });
      if (this.preview) {
        this.preview = {
          ...this.preview,
          files: this.preview.files.map((file) =>
            file.path === saved.path
              ? { ...file, size: saved.size, sha256: saved.sha256 }
              : file,
          ),
          preview:
            this.preview.preview.path === saved.path
              ? saved
              : this.preview.preview,
        };
      }
      await this.load();
    } catch (error) {
      this.fileError = (error as Error).message;
    } finally {
      this.fileSaving = false;
    }
  }
  private get isDirty() {
    return Boolean(
      this.selectedFile && this.editorContent !== this.selectedFile.content,
    );
  }
  private async confirmDelete() {
    const skill = this.deleteTarget;
    if (!skill) return;
    this.busy = skill.name;
    try {
      await api.deleteSkill(skill.name);
      this.success = i18n.t("skillDeleted");
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.busy = "";
      this.deleteTarget = null;
    }
  }
  private async importFile(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.busy = "import";
    try {
      await api.importSkill(file);
      this.success = i18n.t("skillImported");
      await this.load();
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.busy = "";
      input.value = "";
    }
  }
  private selectImportFile() {
    if (this.busy !== "import") {
      this.querySelector<HTMLInputElement>("#skill-import-file")?.click();
    }
  }
  private get totalPages() {
    return pageCount(this.skills.length);
  }
  private get visibleSkills() {
    return pageItems(this.skills, this.page);
  }
  private localizedDescription(skill: SkillRecord) {
    return i18n.language === "zh-CN"
      ? skill.description_zh || skill.description
      : skill.description || skill.description_zh;
  }
  render() {
    return html`
      <page-heading
        heading=${i18n.t("skillsTitle")}
        description=${i18n.t("skillsDescription")}
      >
        <input
          id="skill-import-file"
          hidden
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          tabindex="-1"
          @change=${this.importFile}
        />
        <a2k-button
          ?disabled=${this.busy === "import"}
          @click=${this.selectImportFile}
          >${
            this.busy === "import" ? i18n.t("working") : i18n.t("skillImport")
          }</a2k-button
        >
      </page-heading>
      ${this.error ? html`<p class="status status-error">${this.error}</p>` : null}
      ${this.success ? html`<p class="status status-success">${this.success}</p>` : null}
      ${
        this.loading
          ? html`<a2k-progress></a2k-progress>`
          : html` <surface-window
                class="skills-inventory"
                heading=${i18n.t("skillsInventory")}
              >
                <div class="table-wrap">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>${i18n.t("name")}</th>
                        <th>${i18n.t("skillDescription")}</th>
                        <th>${i18n.t("skillValidation")}</th>
                        <th>${i18n.t("skillUpdated")}</th>
                        <th>${i18n.t("state")}</th>
                        <th>${i18n.t("actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${
                        this.visibleSkills.length
                          ? this.visibleSkills.map(
                              (skill) =>
                                html`<tr>
                                  <td><code>${skill.name}</code></td>
                                  <td>
                                    ${this.localizedDescription(skill) || "-"}
                                  </td>
                                  <td>${skill.validation_status}</td>
                                  <td>${dateTime(skill.updated_at)}</td>
                                  <td>
                                    ${skill.enabled ? i18n.t("enabled") : i18n.t("disabled")}
                                  </td>
                                  <td>
                                    <a2k-button
                                      size="small"
                                      @click=${() => this.openPreview(skill)}
                                      >${i18n.t("details")}</a2k-button
                                    >
                                    <a2k-button
                                      size="small"
                                      ?disabled=${this.busy === skill.name}
                                      @click=${() => this.toggle(skill)}
                                      >${skill.enabled ? i18n.t("disable") : i18n.t("enable")}</a2k-button
                                    >
                                    <a2k-button
                                      size="small"
                                      @click=${() => this.removeSkill(skill)}
                                      >${i18n.t("remove")}</a2k-button
                                    >
                                  </td>
                                </tr>`,
                            )
                          : html`<tr>
                              <td colspan="6">${i18n.t("skillEmpty")}</td>
                            </tr>`
                      }
                    </tbody>
                  </table>
                </div>
              </surface-window>
              <app-pagination
                .page=${this.page}
                .totalPages=${this.totalPages}
                .busy=${this.loading}
                @page-change=${(event: CustomEvent<{ page: number }>) => {
                  this.page = event.detail.page;
                }}
              ></app-pagination>`
      }
      <app-dialog
        .open=${Boolean(this.previewTarget)}
        heading=${i18n.t("skillPreviewTitle", {
          name: this.previewTarget?.name || "",
        })}
        cancel-label=${i18n.t("close")}
        ?hide-confirm=${true}
        @dialog-cancel=${this.closePreview}
      >
        <section
          class="skill-preview dialog-content-container"
          aria-live="polite"
        >
          ${
            this.previewLoading
              ? html`<div class="skill-preview-loading">
                  <a2k-progress></a2k-progress>
                  <span>${i18n.t("skillPreviewLoading")}</span>
                </div>`
              : this.previewError
                ? html`<p class="status status-error">${this.previewError}</p>`
                : this.preview
                  ? html`
                      <dl class="skill-preview-meta">
                        <dt>${i18n.t("name")}</dt>
                        <dd><code>${this.preview.name}</code></dd>
                        <dt>${i18n.t("skillDescription")}</dt>
                        <dd>
                          ${this.localizedDescription(this.preview) || "-"}
                        </dd>
                        <dt>${i18n.t("state")}</dt>
                        <dd>
                          ${
                            this.preview.enabled
                              ? i18n.t("enabled")
                              : i18n.t("disabled")
                          }
                        </dd>
                        <dt>${i18n.t("skillValidation")}</dt>
                        <dd>${this.preview.validation_status}</dd>
                        <dt>${i18n.t("skillUpdated")}</dt>
                        <dd>${dateTime(this.preview.updated_at)}</dd>
                        <dt>${i18n.t("skillFileCount")}</dt>
                        <dd>${this.preview.file_count}</dd>
                        <dt>${i18n.t("skillSize")}</dt>
                        <dd>${this.preview.size}</dd>
                      </dl>
                      <div class="skill-editor">
                        <nav
                          class="skill-file-tree"
                          aria-label=${i18n.t("skillFiles")}
                        >
                          <h3>${i18n.t("skillFiles")}</h3>
                          ${
                            this.safeFiles().some((file) => !file.editable)
                              ? html`<p class="skill-file-tree-hint">
                                  ${i18n.t("skillFileReadOnly")}
                                </p>`
                              : null
                          }
                          ${
                            this.safeFiles().length
                              ? html`<a2k-file-tree
                                  .files=${this.safeFiles()}
                                  height="auto"
                                  max-height="100%"
                                  aria-label=${i18n.t("skillFiles")}
                                  @file-select=${(
                                    event: CustomEvent<SkillFileSummary>,
                                  ) => this.selectFile(event.detail)}
                                ></a2k-file-tree>`
                              : html`<p>${i18n.t("skillFilesEmpty")}</p>`
                          }
                        </nav>
                        <section class="skill-file-editor">
                          <header>
                            <h3>
                              ${
                                this.selectedFile?.path ||
                                i18n.t("skillFileSelect")
                              }
                            </h3>
                            ${
                              this.isDirty
                                ? html`<span class="skill-dirty"
                                    >${i18n.t("skillFileUnsaved")}</span
                                  >`
                                : null
                            }
                          </header>
                          ${
                            this.fileError
                              ? html`<p class="status status-error">
                                  ${this.fileError}
                                </p>`
                              : null
                          }
                          ${
                            this.fileLoading
                              ? html`<div class="skill-preview-loading">
                                  <a2k-progress></a2k-progress>
                                  <span>${i18n.t("skillFileLoading")}</span>
                                </div>`
                              : this.selectedFile
                                ? html`
                                    ${keyed(`${this.previewTarget?.name}:${this.selectedFile.path}`, this.fileEditor())}
                                    <div class="skill-editor-actions">
                                      <a2k-button
                                        ?disabled=${
                                          !this.isDirty || this.fileSaving
                                        }
                                        @click=${this.cancelEdit}
                                        >${i18n.t("cancel")}</a2k-button
                                      >
                                      <a2k-button
                                        ?disabled=${
                                          !this.isDirty || this.fileSaving
                                        }
                                        @click=${this.saveFile}
                                        >${
                                          this.fileSaving
                                            ? i18n.t("working")
                                            : i18n.t("save")
                                        }</a2k-button
                                      >
                                    </div>
                                  `
                                : html`<p>${i18n.t("skillFileSelect")}</p>`
                          }
                        </section>
                      </div>
                    `
                  : null
          }
        </section>
      </app-dialog>
      <app-dialog
        .open=${Boolean(this.deleteTarget)}
        heading=${i18n.t("remove")}
        message=${this.deleteTarget ? i18n.t("skillConfirmDelete", { name: this.deleteTarget.name }) : ""}
        confirm-label=${i18n.t("remove")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("working")}
        ?busy=${Boolean(this.busy)}
        @dialog-cancel=${() => (this.deleteTarget = null)}
        @dialog-confirm=${this.confirmDelete}
      ></app-dialog>
    `;
  }
}
